/**
 * AFW Search Enhancer v3
 * Vehicle year/model filtering, heading rewrite, filter notice,
 * and contact banner for search results pages.
 *
 * Category chip bar and brand filtering are now handled by the
 * afw-collection-sidebar component. This script focuses on:
 *   - Vehicle year + model client-side filtering (hide non-matching products)
 *   - Search heading rewrite (clean up Shopify's raw query display)
 *   - Filter notice banner (X of Y results matching your vehicle)
 *   - Contact banner for no-results / all-hidden scenarios
 */
(function () {
  'use strict';

  var isSearch = window.location.pathname === '/search';
  var isTagFiltered = /\/collections\/[^/]+\/(?:make:|model:|year:)/.test(window.location.pathname);
  if (!isSearch && !isTagFiltered) return;

  /* ── URL Parameter Helpers ─────────────────────────────── */

  function getParam(name) {
    return new URLSearchParams(window.location.search).get(name) || '';
  }

  function getVehicle() {
    var year = getParam('year'), make = getParam('make'), model = getParam('model');
    if (year && make && model) return { year: year, make: make, model: model };
    /* v16: Parse from tag-filtered collection URL path */
    var pathMatch = window.location.pathname.match(/\/collections\/[^/]+\/(.+)/);
    if (pathMatch) {
      var tagStr = decodeURIComponent(pathMatch[1]);
      var tags = tagStr.split('+');
      var py = '', pm = '', pmd = '';
      tags.forEach(function(t) {
        var val = t.split(':').slice(1).join(':');
        if (t.indexOf('year:') === 0) py = val;
        else if (t.indexOf('make:') === 0) pm = val;
        else if (t.indexOf('model:') === 0) pmd = val;
      });
      if (pm && pmd) return { year: py, make: pm, model: pmd };
    }
    try {
      var s = sessionStorage.getItem('afw_vehicle');
      if (s) return JSON.parse(s);
    } catch (e) {}
    return null;
  }

  function getBaseQuery() {
    var base = getParam('afw_base');
    if (base) return base;
    var q = getParam('q');
    q = q.replace(/vendor:"[^"]*"\s*/g, '');
    return q.replace(/\s+/g, ' ').trim();
  }

  function escHtml(str) {
    var d = document.createElement('div');
    d.appendChild(document.createTextNode(str));
    return d.innerHTML;
  }

  /* ── Year Range Parsing + Vehicle Filtering ────────────── */

  function normalizeYear(y) {
    var n = parseInt(y, 10);
    if (isNaN(n)) return null;
    if (n < 100) return n <= 40 ? 2000 + n : 1900 + n;
    return n;
  }

  function parseYearRange(title) {
    var m = title.match(/\((\d{2,4})\s*[-–]\s*(\d{2,4})\)/);
    if (m) {
      var a = normalizeYear(m[1]), b = normalizeYear(m[2]);
      if (a !== null && b !== null) {
        if (b < a) b += 100;
        return { min: Math.min(a, b), max: Math.max(a, b) };
      }
    }
    var s = title.match(/\((\d{4})\)/);
    if (s) {
      var yr = parseInt(s[1], 10);
      if (yr >= 1950 && yr <= 2100) return { min: yr, max: yr };
    }
    return null;
  }

  function normalizeModel(name) {
    return name.toLowerCase().replace(/[-\s]/g, '');
  }

  function titleMatchesModel(title, userModel) {
    var tl = title.toLowerCase(), ml = userModel.toLowerCase();
    if (tl.indexOf(ml) !== -1) return true;
    var tn = normalizeModel(title), mn = normalizeModel(userModel);
    if (tn.indexOf(mn) !== -1) return true;
    var parts = tl.split('/');
    for (var i = 0; i < parts.length; i++) {
      var p = parts[i].trim();
      if (p.indexOf(ml) !== -1 || normalizeModel(p).indexOf(mn) !== -1) return true;
    }
    return false;
  }

  var filterNoticeEl = null;
  var filterActive = true;

  function filterByVehicle(vehicle) {
    if (!vehicle) return;
    var userYear = parseInt(vehicle.year, 10);
    if (isNaN(userYear)) return;

    var cards = document.querySelectorAll('.t4s-product.t4s-pr-grid');
    if (cards.length === 0) return;

    var total = 0, shown = 0, hidden = 0;

    cards.forEach(function (card) {
      total++;
      var titleEl = card.querySelector('.t4s-product-title');
      if (!titleEl) { shown++; return; }
      var title = titleEl.textContent.trim();
      if (!title) { shown++; return; }

      var yearRange = parseYearRange(title);
      var modelMatch = titleMatchesModel(title, vehicle.model);
      var shouldHide = false;

      if (yearRange !== null) {
        if (userYear < yearRange.min || userYear > yearRange.max) shouldHide = true;
      }
      if (!modelMatch) shouldHide = true;

      if (filterActive && shouldHide) {
        card.style.display = 'none';
        card.setAttribute('data-afw-filtered', 'true');
        hidden++;
      } else {
        card.style.display = '';
        card.removeAttribute('data-afw-filtered');
        shown++;
      }
    });

    // If every product was hidden, auto-expand but show clear warning
    var allHidden = (shown === 0 && hidden > 0 && filterActive);
    if (allHidden) {
      filterActive = false;
      cards.forEach(function (card) { card.style.display = ''; });
      shown = total;
    }

    updateFilterNotice(vehicle, shown, total, hidden, allHidden);
  }

  function updateFilterNotice(vehicle, shown, total, hidden, allHidden) {
    if (hidden === 0) {
      if (filterNoticeEl) filterNoticeEl.style.display = 'none';
      return;
    }
    var name = vehicle.year + ' ' + vehicle.make + ' ' + vehicle.model;

    if (!filterNoticeEl) {
      filterNoticeEl = document.createElement('div');
      filterNoticeEl.className = 'afw-se-filter-notice';
      var grid = document.querySelector('.t4s_box_pr_grid');
      if (grid) grid.parentNode.insertBefore(filterNoticeEl, grid);
    }

    filterNoticeEl.style.display = '';

    // When ALL products were hidden (no matches on page), show a clear warning
    if (allHidden) {
      filterNoticeEl.innerHTML =
        '<div class="afw-se-filter-notice__inner afw-se-filter-notice__inner--warn">' +
          '<i class="las la-exclamation-triangle"></i>' +
          '<span>No exact matches for your <strong>' + escHtml(name) +
          '</strong> found on this page. Showing all <strong>' + total + '</strong> results below.' +
          ' Try searching specifically for your vehicle above.</span>' +
        '</div>';
      return;
    }

    filterNoticeEl.innerHTML =
      '<div class="afw-se-filter-notice__inner">' +
        '<i class="las la-filter"></i>' +
        '<span>Showing <strong>' + shown + '</strong> of ' + total +
        ' results matching your <strong>' + escHtml(name) + '</strong></span>' +
        '<a href="#" class="afw-se-filter-notice__toggle">' +
        (filterActive ? 'Show all results' : 'Filter for my vehicle') +
        '</a></div>';

    filterNoticeEl.querySelector('.afw-se-filter-notice__toggle')
      .addEventListener('click', function (e) {
        e.preventDefault();
        filterActive = !filterActive;
        if (!filterActive) {
          document.querySelectorAll('.t4s-product[data-afw-filtered]')
            .forEach(function (c) { c.style.display = ''; });
          this.textContent = 'Filter for my vehicle';
          filterNoticeEl.querySelector('span').innerHTML =
            'Showing <strong>' + total + '</strong> of ' + total +
            ' results &mdash; <strong>' + hidden + '</strong> may not fit your <strong>' +
            escHtml(name) + '</strong>';
        } else {
          filterByVehicle(vehicle);
        }
      });
  }

  function observeNewProducts(vehicle) {
    if (!vehicle) return;
    var grid = document.querySelector('.t4s_box_pr_grid');
    if (!grid) return;
    var timer;
    new MutationObserver(function () {
      clearTimeout(timer);
      timer = setTimeout(function () {
        if (filterActive) filterByVehicle(vehicle);
      }, 300);
    }).observe(grid, { childList: true });
  }

  /* ── Contact Banner ─────────────────────────────────────── */

  function injectContactBanner(vehicleName) {
    var banner = document.createElement('div');
    banner.className = 'afw-se-contact';
    banner.innerHTML =
      '<div class="afw-se-contact__icon"><i class="las la-car"></i></div>' +
      '<h3 class="afw-se-contact__title">We carry more inventory than what\'s on our website!</h3>' +
      '<p class="afw-se-contact__text">Our experts can help you find exactly what you need for your <strong>' +
        escHtml(vehicleName) + '</strong>. Give us a call or stop by our shop.</p>' +
      '<div class="afw-se-contact__actions">' +
        '<a href="tel:9132912027" class="afw-se-contact__btn afw-se-contact__btn--call">' +
          '<i class="las la-phone"></i> Call (913) 291-2027</a>' +
        '<a href="https://www.google.com/maps/dir/?api=1&destination=12310+W+62nd+Ter+Shawnee+KS+66216" ' +
          'target="_blank" rel="noopener" class="afw-se-contact__btn afw-se-contact__btn--visit">' +
          '<i class="las la-map-marker-alt"></i> Visit Our Shop</a>' +
      '</div>' +
      '<p class="afw-se-contact__address">12310 W 62nd Ter, Shawnee, KS 66216 &bull; Mon-Fri 9-6, Sat 10-4</p>' +
      '<p class="afw-se-contact__browse">Browse our full catalog below &mdash; use the sidebar filters to narrow your search.</p>';

    var mainArea = document.querySelector('.t4s-main-collection-search');
    var section = mainArea ? mainArea.closest('.shopify-section') : null;
    var inner = section ? section.querySelector('.t4s-section-inner') : null;
    var container = inner ? inner.querySelector('.t4s-container, .t4s-container-wrap') : null;
    var rowDiv = inner ? inner.querySelector('.t4s-row') : null;

    if (container) container.insertBefore(banner, container.firstChild);
    else if (rowDiv) inner.insertBefore(banner, rowDiv);
    else if (mainArea) mainArea.parentNode.insertBefore(banner, mainArea);
  }

  /* ── Heading Rewrite ───────────────────────────────────── */

  function rewriteHeading(vehicle, baseQuery, isNoMatch, activeBrand) {
    var heading = document.querySelector('.t4s-title-head');
    if (!heading) return;

    var countMatch = heading.textContent.match(/(\d+)\s+(?:Search\s+)?Results?/i);
    var count = countMatch ? countMatch[1] : '';

    var parts = [];

    if (count) parts.push(count + ' ');

    if (vehicle) {
      var vehicleName = (vehicle.year ? vehicle.year + ' ' : '') + vehicle.make + ' ' + vehicle.model;
      if (isTagFiltered) {
        parts.push('Parts for ');
      } else {
        parts.push((isNoMatch ? 'Products for ' : 'Results for '));
      }
      parts.push('<span>' + escHtml(vehicleName) + '</span>');
    } else if (baseQuery) {
      parts.push('Results for &ldquo;' + escHtml(baseQuery) + '&rdquo;');
    } else if (isTagFiltered) {
      parts.push('Parts');
    } else {
      parts.push('Search Results');
    }

    if (activeBrand) {
      parts.push(' by <span>' + escHtml(activeBrand) + '</span>');
    }

    heading.innerHTML = parts.join('');
    heading.classList.add('afw-se-heading');
  }

  /* ── Init ──────────────────────────────────────────────── */

  function init() {
    var vehicle     = getVehicle();
    var activeBrand = getParam('afw_brand');
    var isNoMatch   = getParam('vf_no_match') === '1';
    var baseQuery   = getBaseQuery();

    // Save vehicle to session
    if (vehicle) {
      try { sessionStorage.setItem('afw_vehicle', JSON.stringify(vehicle)); } catch (e) {}
    }

    // Rewrite heading to show clean text
    rewriteHeading(vehicle, baseQuery, isNoMatch, activeBrand);

    // No-results handling
    var bannerName = vehicle
      ? (vehicle.year ? vehicle.year + ' ' : '') + vehicle.make + ' ' + vehicle.model
      : 'your vehicle';

    if (isNoMatch) {
      injectContactBanner(bannerName);
    } else {
      var empty = document.querySelector('.t4s-coll-empty');
      if (empty) {
        injectContactBanner(bannerName);
        empty.style.display = 'none';
      }
    }

    /* v16: On tag-filtered collection pages, vehicle filtering is ALREADY done
       server-side by Shopify's tag filter. No client-side filtering needed!
       Only apply client-side filtering on /search pages. */
    if (isSearch && vehicle && !isNoMatch) {
      filterByVehicle(vehicle);
      observeNewProducts(vehicle);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
